/**
 * Visit Notes Service Implementation (Core Layer)
 *
 * Business logic for visit notes management
 */

import type {
  IVisitNotesService,
  IVisitNotesRepository,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  ICreateVisitNoteResponse,
  IUpdateVisitNoteResponse,
  IGetAllVisitNotesResponse,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  IGetSidePeekProfileResponse,
  IGenerateSidePeekProfileResponse,
  IUpdateSidePeekProfileResponse,
  IGetSafetyNoteResponse,
  IUpdateSafetyNoteResponse,
  IGetCodeStatusResponse,
  IUpdateCodeStatusResponse,
  CodeStatusValue,
} from "../ports/driver/VisitNotesService";

export const VisitNotesServiceImplFactory = ({
  visitNotesRepository,
}: {
  visitNotesRepository: IVisitNotesRepository;
}): IVisitNotesService => {
  /**
   * Create a new visit note
   */
  const createVisitNote = async (
    request: ICreateVisitNoteRequest
  ): Promise<ICreateVisitNoteResponse> => {
    try {
      const data = await visitNotesRepository.createVisitNoteAPI(request);

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to create visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to create visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update an existing visit note
   */
  const updateVisitNote = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IUpdateVisitNoteResponse> => {
    try {
      const data = await visitNotesRepository.updateVisitNoteAPI(
        clinicId,
        patientId,
        noteId,
        request
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get all visit notes for a clinic
   */
  const getAllVisitNotes = async (
    clinicId: number
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.getAllVisitNotesAPI(clinicId);

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get all visit notes:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get all visit notes",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get a single visit note
   */
  const getVisitNote = async (
    noteId: number
  ): Promise<{ success: boolean; data?: any; error?: any }> => {
    try {
      const data = await visitNotesRepository.getVisitNoteAPI(noteId);

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get visit note:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Summarize a narrative for sharing
   */
  const summarizeNarrative = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    try {
      const response = await visitNotesRepository.summarizeNarrativeAPI(
        request
      );

      return response;
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to summarize narrative:",
        error
      );
      return {
        success: false,
        error: error?.message || "Failed to summarize narrative",
      };
    }
  };

  /**
   * Send visit note PDF via eFax
   */
  const sendEfax = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    try {
      const response = await visitNotesRepository.sendEfaxAPI(request);
      return response;
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to send eFax:", error);
      throw error;
    }
  };

  /**
   * Get Side Peek Profile for a patient
   */
  const getSidePeekProfile = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSidePeekProfileResponse> => {
    try {
      const result = await visitNotesRepository.getSidePeekProfileAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data: result.sidepeek_profile || undefined,
        patient_dob: result.patient_dob,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   */
  const generateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<IGenerateSidePeekProfileResponse> => {
    try {
      const data = await visitNotesRepository.generateSidePeekProfileAPI(
        clinicId,
        patientId,
        noteId,
        section,
        model
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to generate side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to generate side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   */
  const updateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
    }
  ): Promise<IUpdateSidePeekProfileResponse> => {
    try {
      const data = await visitNotesRepository.updateSidePeekProfileAPI(
        clinicId,
        patientId,
        updateData
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Safety Note for a patient
   */
  const getSafetyNote = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSafetyNoteResponse> => {
    try {
      const data = await visitNotesRepository.getSafetyNoteAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get safety note:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get safety note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Safety Note for a patient
   */
  const updateSafetyNote = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<IUpdateSafetyNoteResponse> => {
    try {
      const data = await visitNotesRepository.updateSafetyNoteAPI(
        clinicId,
        patientId,
        content
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update safety note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update safety note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Code Status for a patient
   */
  const getCodeStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetCodeStatusResponse> => {
    try {
      const data = await visitNotesRepository.getCodeStatusAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get code status:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get code status",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Code Status for a patient
   */
  const updateCodeStatus = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<IUpdateCodeStatusResponse> => {
    try {
      const data = await visitNotesRepository.updateCodeStatusAPI(
        clinicId,
        patientId,
        status,
        otherStatus
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update code status:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update code status",
          code: error?.code,
        },
      };
    }
  };

  return {
    createVisitNote,
    updateVisitNote,
    getAllVisitNotes,
    getVisitNote,
    summarizeNarrative,
    sendEfax,
    getSidePeekProfile,
    generateSidePeekProfile,
    updateSidePeekProfile,
    getSafetyNote,
    updateSafetyNote,
    getCodeStatus,
    updateCodeStatus,
  };
};
